import { type Periodo, formatarDuracao } from '@projetive-op/domain'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { Download } from 'lucide-react'
import { useState } from 'react'

import { BarraTitulo } from '@/components/cadastros/base'
import { SeletorPeriodo, referenciaDe } from '@/components/gestao/seletor-periodo'
import { Button } from '@/components/ui/button'
import { Gaveta } from '@/components/ui/gaveta'
import { Numero } from '@/components/ui/numero'
import { Cabecalho, Carregando, Linha, Pilula, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { baixarCsv, paraCsv } from '@/lib/csv'
import { ROTULO_AUSENCIA, diaCurto, hojeIso, nota, pct, pctTitulo } from '@/lib/formato'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/bonus')({ component: Bonus })

type Relatorio = NonNullable<ReturnType<typeof useRelatorio>['data']>
type OperadorDoRelatorio = Relatorio['operadores'][number]

function useRelatorio(periodo: Periodo | undefined, referencia: Date) {
  const trpc = useTRPC()
  return useQuery(
    trpc.avaliacao.relatorioBonus.queryOptions({
      ...(periodo ? { periodo } : {}),
      referencia,
    }),
  )
}

/**
 * Relatório de bônus (RF-D08): por período, nota e faixa de cada operador
 * com dias trabalhados e faltas. Só gestão e diretoria — a decisão de pagar
 * é humana e fica fora do sistema.
 */
function Bonus() {
  const [periodo, setPeriodo] = useState<Periodo | undefined>(undefined)
  const [data, setData] = useState(hojeIso)
  const [detalhe, setDetalhe] = useState<OperadorDoRelatorio | null>(null)
  const relatorio = useRelatorio(periodo, referenciaDe(data))
  const r = relatorio.data

  const exportar = () => {
    if (!r) return
    const linhas = [
      [
        'Operador',
        'Turno',
        'Dias trabalhados',
        'Faltas',
        'Ausências',
        'Fichas concluídas',
        'Peças apontadas',
        'Tempo logado (h)',
        'Tempo produzindo (h)',
        'Hora extra (h)',
        'Eficiência',
        'Produtividade',
        'Pontualidade',
        'Nota',
        'Faixa',
      ],
      ...r.operadores.map((o) => [
        o.operador.nome,
        o.operador.turno,
        o.diasTrabalhados,
        o.faltas,
        o.diasDeAusencia,
        o.fichasConcluidas,
        o.pecasApontadas,
        Math.round((o.tempoLogadoSeg / 3600) * 100) / 100,
        Math.round((o.tempoProdutivoSeg / 3600) * 100) / 100,
        Math.round((o.tempoAlemDoTurnoSeg / 3600) * 100) / 100,
        o.eficiencia == null ? null : Math.round(o.eficiencia * 1000) / 1000,
        o.produtividade == null ? null : Math.round(o.produtividade * 1000) / 1000,
        o.pontualidadeMedia == null ? null : Math.round(o.pontualidadeMedia * 1000) / 1000,
        o.nota,
        o.faixa?.nome ?? '',
      ]),
    ]
    const inicio = new Date(r.periodo.inicio).toISOString().slice(0, 10)
    baixarCsv(`bonus-${inicio}.csv`, paraCsv(linhas))
  }

  return (
    <>
      <BarraTitulo
        titulo="Relatório de bônus"
        descricao="Nota e faixa por operador no período do bônus. O sistema classifica; a decisão de pagar é da gestão. Nada disto aparece no tablet."
      />
      <SeletorPeriodo
        periodo={periodo}
        onPeriodo={setPeriodo}
        data={data}
        onData={setData}
        opcaoPadrao="Período do bônus"
        intervalo={r?.periodo}
        sufixo={r && periodo === undefined ? `bônus ${r.periodicidade.toLowerCase()}` : undefined}
      >
        <Button
          size="md"
          variant="secondary"
          className="ml-auto"
          onClick={exportar}
          disabled={!r || r.operadores.length === 0}
        >
          <Download className="size-4" aria-hidden /> Exportar CSV
        </Button>
      </SeletorPeriodo>

      <Tabela>
        <Cabecalho>
          <Th>Operador</Th>
          <Th className="text-right">Dias</Th>
          <Th className="text-right">Faltas</Th>
          <Th className="text-right">Ausências</Th>
          <Th className="text-right">Fichas</Th>
          <Th className="text-right">Eficiência</Th>
          <Th className="text-right">Produtividade</Th>
          <Th className="text-right">Pontualidade</Th>
          <Th className="text-right">Nota</Th>
          <Th>Faixa</Th>
        </Cabecalho>
        <tbody>
          {r?.operadores.map((o) => (
            <Linha
              key={o.operador.id}
              className="cursor-pointer"
              onClick={() => setDetalhe(o)}
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && setDetalhe(o)}
            >
              <Td className="min-w-48">
                <span className="font-medium">{o.operador.nome}</span>
                <span className="block text-xs text-fg-subtle">
                  {o.operador.turno ?? 'sem turno'}
                  {!o.operador.ativo && ' · desativado'}
                </span>
              </Td>
              <Td numerico className="text-right">
                {o.diasTrabalhados}
              </Td>
              <Td
                numerico
                className={`text-right ${o.faltas > 0 ? 'font-semibold text-vermelho' : ''}`}
              >
                {o.faltas}
              </Td>
              <Td numerico className="text-right text-fg-muted">
                {o.diasDeAusencia}
              </Td>
              <Td numerico className="text-right">
                {o.fichasConcluidas}
              </Td>
              <Td numerico className="text-right" title={pctTitulo(o.eficiencia)}>
                {pct(o.eficiencia)}
              </Td>
              <Td numerico className="text-right">
                {pct(o.produtividade)}
              </Td>
              <Td numerico className="text-right">
                {pct(o.pontualidadeMedia)}
              </Td>
              <Td numerico className="text-right text-lg font-bold">
                {nota(o.nota)}
              </Td>
              <Td>
                {o.faixa ? (
                  <Pilula tom="verde">{o.faixa.nome}</Pilula>
                ) : (
                  <span className="text-fg-subtle">—</span>
                )}
              </Td>
            </Linha>
          ))}
          {relatorio.isPending && <Carregando colunas={10} />}
          {r?.operadores.length === 0 && <Vazio colunas={10}>Nenhum operador.</Vazio>}
        </tbody>
      </Tabela>
      {r?.criterios.length === 0 && (
        <p role="alert" className="mt-3 text-sm font-medium text-amarelo">
          Nenhum critério ativo em Avaliação: as notas ficam vazias.
        </p>
      )}

      <Gaveta
        aberta={detalhe != null}
        titulo={detalhe ? `${detalhe.operador.nome} · dia a dia` : ''}
        onFechar={() => setDetalhe(null)}
        largura="lg"
      >
        {detalhe && (
          <div className="flex flex-col gap-4 text-sm">
            <div className="grid grid-cols-3 gap-3">
              <Numero
                tamanho="sm"
                fundo="elevado"
                rotuloEmCima
                alinhar="esquerda"
                rotulo="Nota do período"
                valor={nota(detalhe.nota)}
              />
              <Numero
                tamanho="sm"
                fundo="elevado"
                rotuloEmCima
                alinhar="esquerda"
                rotulo="Faixa"
                valor={detalhe.faixa?.nome ?? '—'}
              />
              <Numero
                tamanho="sm"
                fundo="elevado"
                rotuloEmCima
                alinhar="esquerda"
                rotulo="Faltas"
                valor={detalhe.faltas}
              />
              <Numero
                tamanho="sm"
                fundo="elevado"
                rotuloEmCima
                alinhar="esquerda"
                rotulo="Tempo logado"
                valor={formatarDuracao(detalhe.tempoLogadoSeg)}
              />
              <Numero
                tamanho="sm"
                fundo="elevado"
                rotuloEmCima
                alinhar="esquerda"
                rotulo="Produzindo"
                valor={formatarDuracao(detalhe.tempoProdutivoSeg)}
              />
              <Numero
                tamanho="sm"
                fundo="elevado"
                rotuloEmCima
                alinhar="esquerda"
                rotulo="Hora extra"
                valor={formatarDuracao(detalhe.tempoAlemDoTurnoSeg)}
              />
              <Numero
                tamanho="sm"
                fundo="elevado"
                rotuloEmCima
                alinhar="esquerda"
                rotulo="Atraso médio"
                valor={
                  detalhe.atrasoMedioSeg == null ? '—' : formatarDuracao(detalhe.atrasoMedioSeg)
                }
              />
            </div>
            <table className="w-full">
              <thead className="text-left text-xs tracking-wider text-fg-muted uppercase">
                <tr>
                  <th className="py-1">Dia</th>
                  <th className="py-1">Situação</th>
                  <th className="py-1 text-right">Atraso</th>
                  {r?.criterios.map((c) => (
                    <th key={c.codigo} className="py-1 text-right">
                      {c.nome.split(' ')[0]}
                    </th>
                  ))}
                  <th className="py-1 text-right">Nota</th>
                </tr>
              </thead>
              <tbody>
                {detalhe.dias.map((d) => (
                  <tr
                    key={d.data}
                    className={`border-t border-border ${d.falta ? 'text-vermelho' : ''}`}
                  >
                    <td className="py-1 tabular">{diaCurto(d.data)}</td>
                    <td className="py-1">
                      {d.trabalhou
                        ? 'Trabalhou'
                        : d.falta
                          ? 'Falta'
                          : d.ausencia
                            ? ROTULO_AUSENCIA[d.ausencia.tipo]
                            : '—'}
                      {d.tolerancia > 0 && (
                        <span className="ml-1 text-xs text-fg-subtle">
                          (curva {Math.round(d.tolerancia * 100)}%)
                        </span>
                      )}
                    </td>
                    <td className="py-1 text-right tabular">
                      {d.atrasoSeg == null ? '—' : formatarDuracao(d.atrasoSeg)}
                    </td>
                    {d.criterios.map((c) => (
                      <td key={c.codigo} className="py-1 text-right tabular">
                        {pct(c.valor)}
                        {c.peso !== r?.criterios.find((x) => x.codigo === c.codigo)?.peso && (
                          <span className="ml-1 text-xs text-fg-subtle">
                            ×{Math.round(c.peso * 100) / 100}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="py-1 text-right font-semibold tabular">{nota(d.nota)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Gaveta>
    </>
  )
}
